import { AI_GENERATION_LANGUAGES, AI_IMAGE_SOURCES, ThemeAiRules } from "common/aiConstants";
import { sanitizeAiRules } from "common/utils/sanitizeAiRules";

/** The theme field holding every AI setting, and its name when a theme is inlined on a presentation. */
export const THEME_AI_RULES_KEY = "aiRules";
export const PRESENTATION_AI_RULES_KEY = "theme_aiRules";

/** The free text that ends up inside a generation prompt, and so is sanitized. */
export const AI_GUIDANCE_KEYS = ["voiceAndTone", "presentationInstructions"] as const;

/**
 * The rules that take the brand guardrails permission to write: the theme
 * editor's whole AI Writing section. `imageSource` is deliberately not one of
 * them - it belongs to the Images section, which every plan is entitled to.
 */
export const BRAND_GUARDED_AI_RULES_KEYS = [...AI_GUIDANCE_KEYS, "language"] as const;

const ALLOWED_ENUM_VALUES: { [key: string]: readonly string[] } = {
    language: AI_GENERATION_LANGUAGES,
    imageSource: AI_IMAGE_SOURCES
};

/**
 * Cleans an `aiRules` value written by a client: guidance text is sanitized and
 * enum values are rejected rather than stored. Keys the update doesn't mention
 * are left alone, an explicit null clears one.
 */
export function sanitizeThemeAiRules(rules: any): { rules: ThemeAiRules | null, invalidKeys: string[] } {
    if (!rules || typeof rules !== "object") {
        return { rules: null, invalidKeys: [] };
    }

    const sanitized: ThemeAiRules = {};
    const invalidKeys: string[] = [];

    for (const key of AI_GUIDANCE_KEYS) {
        if (key in rules) {
            sanitized[key] = sanitizeAiRules(rules[key]) ?? null;
        }
    }

    for (const [key, allowedValues] of Object.entries(ALLOWED_ENUM_VALUES)) {
        if (!(key in rules)) {
            continue;
        }

        const value = rules[key];
        if (value === null || value === undefined) {
            sanitized[key] = null;
        } else if (allowedValues.includes(value)) {
            sanitized[key] = value;
        } else {
            invalidKeys.push(key);
        }
    }

    return { rules: sanitized, invalidKeys };
}

/**
 * The brand guarded keys the update gives a value to. Clearing one takes nothing
 * away from anyone's generation, so it is not a write that needs permission.
 */
export function getWrittenBrandGuardedKeys(rules: ThemeAiRules | null | undefined): string[] {
    return BRAND_GUARDED_AI_RULES_KEYS.filter(key => Boolean(sanitizeAiRules(rules?.[key])));
}

/** The same rules with the brand guarded settings the writer may not author left out. */
export function omitBrandGuardedAiRules(rules: ThemeAiRules | null | undefined): ThemeAiRules | null {
    if (!rules) {
        return null;
    }

    const remaining = { ...rules };
    for (const key of BRAND_GUARDED_AI_RULES_KEYS) {
        delete remaining[key];
    }

    return remaining;
}

/**
 * Drops the kit's AI rules from a presentation model that is about to leave the
 * app - the player payload, an offline export. They are workspace-internal
 * writing guidance and say nothing about how the deck looks, while the rest of
 * the inlined theme is what a viewer renders with.
 */
export function stripAiRulesFromPresentation<T extends Record<string, any>>(presentation: T): T {
    delete presentation[PRESENTATION_AI_RULES_KEY];
    return presentation;
}

/**
 * The image style a kit asks for, as the generation APIs and the style picker
 * understand it: a preset id, or the prompt behind the literal "custom".
 */
export function resolveThemeAiImageStyle(theme: { imageStyle?: string | null, imageStylePrompt?: string | null }): string | undefined {
    const { imageStyle, imageStylePrompt } = theme;

    if (!imageStyle) {
        return undefined;
    }

    return imageStyle === "custom" ? imageStylePrompt || undefined : imageStyle;
}
